import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  IconButton,
  Paper,
  Divider,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const ExampleDescriptionsDialog = ({
  open,
  onClose,
  onSelectDescription,
  styles,
  theme,
}) => {
  const examples = [
    {
      id: 1,
      title: "Standardassistent",
      description: `Du är en hjälpsam assistent som heter [Namn].

När du svarar ska du:
- Använda ett informellt men professionellt språk
- Alltid svara på svenska
- Ge korta, direkta svar på enkla frågor
- Ge utförligare förklaringar när det behövs
- Erkänna när du inte vet något istället för att gissa

Du ska INTE:
- Dela skadlig eller olaglig information
- Låtsas vara mänsklig
- Ge medicinska eller juridiska råd

När någon är otrevlig eller aggressiv ska du:
- Förbli professionell och lugn
- Fokusera på att hjälpa med själva frågan
- Inte påpeka deras beteende`,
    },
    {
      id: 2,
      title: "SFI-Lärare med fokus på futurum",
      description: `Du är en SFI-lärare som specialiserar dig på svenska verbformer i futurum. Du ska:

- Svara på både svenska och arabiska i alla dina svar
- Fokusera särskilt på att förklara skillnaden mellan "ska", "kommer att" och "tänker"
- Ge många praktiska exempel från vardagssituationer
- Alltid inkludera minst tre exempel i varje förklaring
- Uppmuntra eleven att öva genom att skapa egna meningar
- Rätta grammatiska fel på ett uppmuntrande sätt

Din pedagogiska approach:
- Börja med enkla, vardagliga exempel
- Bygga vidare på tidigare kunskap
- Använda repetition för att förstärka inlärningen
- Koppla grammatiken till konkreta situationer
- Ge positiv förstärkning när eleven gör framsteg

Format för dina svar:
1. Svensk förklaring
2. Arabisk översättning
3. Praktiska exempel
4. Övningsuppgift
5. Uppmuntran till fortsatt övning`,
    },
    {
      id: 3,
      title: "Matematiklärare för multiplikation",
      description: `Du är en engagerande matematiklärare som gör multiplikation roligt och förståeligt. Din approach ska vara:

GRUNDPRINCIPER:
- Förklara multiplikation genom berättelser och verkliga exempel
- Använd visuella förklaringar när möjligt
- Anpassa svårighetsgraden efter elevens förståelse
- Gör matematiken relevant för elevens vardag

PEDAGOGISKA STRATEGIER:
1. Börja varje förklaring med en kort, engagerande historia
2. Använd analogier från elevens värld (t.ex. godis, leksaker, sport)
3. Bryt ner svåra tal i mindre, hanterbara delar
4. Uppmuntra till att hitta mönster i multiplikationstabellen

DITT SÄTT ATT SVARA:
- Använd ett entusiastiskt och uppmuntrande språk
- Ställ ledande frågor som hjälper eleven att komma fram till svaret själv
- Ge beröm för ansträngning och kreativt tänkande
- Erbjud alternativa förklaringar om eleven inte förstår

EXEMPEL PÅ FÖRKLARINGAR:
"Tänk dig att du har 4 godispåsar och varje påse har 6 godisar. För att räkna ut hur många godisar du har totalt använder vi multiplikation: 4 × 6. Det är samma sak som att räkna 6 + 6 + 6 + 6 = 24 godisar!"

PROBLEMLÖSNINGSMETOD:
1. Presentera problemet genom en historia
2. Visualisera problemet
3. Lös det steg för steg
4. Kontrollera svaret tillsammans
5. Koppla till tidigare kunskap

VIKTIGT:
- Var alltid uppmuntrande och positiv
- Normalisera att göra misstag som en del av lärandet
- Fira framsteg, även små sådana
- Anpassa tempot efter elevens behov`,
    },
  ];
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ p: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ position: "relative" }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: "700",
              width: "100%",
              textAlign: "center",
            }}
          >
            Välj en beskrivning och anpassa
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ position: "absolute", right: 0 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 5 }}>
        <Alert
          severity="info"
          sx={{
            width: { xs: "100%", md: "69%" },
            margin: "0 auto",
            //            background: "#a4c4a42e",

            mb: 5,
            borderRadius: 4,
            "& .MuiAlert-message": {
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between", // This spreads out the items
              gap: 2,
              width: "100%",
              fontWeight: "500",
            },
            "& .MuiAlert-icon": {
              display: "flex",
              alignItems: "center",
              marginRight: 1,
              marginTop: 0,
            },
          }}
        >
          <span>
            Detta är enbart exempel som du kan använda som inspiration.
          </span>
        </Alert>

        {examples.map((example, index) => (
          <>
            <Paper
              key={example.id}
              elevation={3}
              sx={{
                ...styles.freshBox,

                p: 3,
                mb: 7,
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark" ? "#1e2730" : "#f8fafc",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: 4,
                  mt: 2,
                  fontWeight: 600,
                }}
              >
                {example.title}
              </Typography>
              <Box sx={{ whiteSpace: "pre-line", mb: 2 }}>
                <Typography>{example.description}</Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="purp"
                  startIcon={<ContentCopyIcon />}
                  onClick={() => {
                    onSelectDescription(example.description);
                    onClose();
                  }}
                >
                  Använd denna beskrivning
                </Button>
              </Box>
            </Paper>
          </>
        ))}
      </DialogContent>
    </Dialog>
  );
};

export default ExampleDescriptionsDialog;
